/**
 * TanStack Query client.
 *
 * Defaults match the watch-surface posture:
 * - refetchOnWindowFocus: true — operators tab between CC and other
 *   tools all day; refetching on focus matches expectation that the
 *   surface reflects the latest state.
 * - retry: 1 — one auto-retry for transient network failures, then
 *   surface the error to the boundary / toast.
 * - 401 errors are not retried; the auth layer maps 401s to throws
 *   and the router redirects to the signed-out view.
 * - staleTime 30s globally; per-surface freshness is driven by each
 *   hook's refetchInterval, not per-query staleTime overrides.
 *
 * Multi-query 401 propagation:
 *   The Shell branches on `useMe` returning UnauthenticatedError.
 *   When a *different* query hits a 401 because the session expired
 *   mid-session, the shell wouldn't notice — it only listens to
 *   `useMe`. Wiring a QueryCache subscriber to invalidate `["me"]`
 *   whenever ANY query throws UnauthenticatedError makes
 *   session-expiry surface app-wide for every future query without
 *   per-query plumbing.
 */

import { QueryClient } from "@tanstack/react-query";
import { UnauthenticatedError } from "./api";

export function createQueryClient(): QueryClient {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
          if (error instanceof UnauthenticatedError) return false;
          return failureCount < 1;
        },
        staleTime: 30_000,
      },
      mutations: {
        retry: false,
      },
    },
  });

  // Subscribe to the QueryCache so any UnauthenticatedError from any
  // query forces useMe to re-fetch. The Shell already branches on the
  // resulting useMe error — this turns "any query saw 401" into "the
  // shell knows about it" without per-query plumbing.
  client.getQueryCache().subscribe((event) => {
    if (event.type !== "updated") return;
    const error = event.query.state.error;
    if (error instanceof UnauthenticatedError) {
      // Avoid infinite loop: don't invalidate if it was the me query
      // itself that errored (TanStack will already surface that).
      const key = event.query.queryKey;
      if (Array.isArray(key) && key[0] === "me") return;
      client.invalidateQueries({ queryKey: ["me"] });
    }
  });

  return client;
}
